import { CommonModule, DOCUMENT } from '@angular/common';
import { ChangeDetectorRef, Component, Inject, OnDestroy, OnInit, ViewChild, inject } from '@angular/core';
import { ActivatedRoute } from '@angular/router';

import { FundingService } from '@bytel/checkout';

import { AppService } from '@services/app.services';
import { PaymentService } from '@services/payment.service';

import { Observable, Subject, catchError, of } from 'rxjs';
import { mergeMap, takeUntil } from 'rxjs/operators';

import { YounitedReason } from '../../interfaces/payment.interface';
import { PaymentErrorModalComponent } from '../../shared/payment-error-modal/payment-error-modal.component';
import { PaymentStep } from '../../shared/stepper/steps/payment.step';
import { BasicObject } from './../../interfaces/checkout.interface';
import { PrismeLoggerService } from './../../services/prisme-logger.service';
import { TagGeneratorServices } from './../../services/tag-generator.services';
import { ACTION_CLICK } from './../../shared/tag/tag.interface';
import { TagService } from './../../shared/tag/tag.service';

@Component({
    selector: 'checkout-payment-redirect',
    templateUrl: './payment-redirect.component.html',
    styleUrl: 'payment-redirect.component.scss',
    imports: [PaymentErrorModalComponent, CommonModule],
    standalone: true,
})
export class PaymentRedirectComponent implements OnInit, OnDestroy {
    @ViewChild('paymentDataFormElement') paymentDataFormElement;
    public paymentData: BasicObject = {
        url: null,
        method: null,
        inputs: [],
    };
    public errorRedirect: boolean = false;
    private page: string = 'paiement_redirect_younited';
    private _ngUnsubscribe$ = new Subject();

    private paymentStep: PaymentStep = inject(PaymentStep);
    @Inject(DOCUMENT) private document: Document = inject(DOCUMENT);
    private fundingService: FundingService = inject(FundingService);
    private paymentService: PaymentService = inject(PaymentService);
    private tagService: TagService = inject(TagService);
    private checkoutTagService: TagGeneratorServices = inject(TagGeneratorServices);
    private ref: ChangeDetectorRef = inject(ChangeDetectorRef);
    private prismeLoggerService: PrismeLoggerService = inject(PrismeLoggerService);
    private route: ActivatedRoute = inject(ActivatedRoute);
    private appService: AppService = inject(AppService);

    ngOnInit(): void {
        this.tagService.send(this.checkoutTagService.getTags(this.page, 'paiement_redirect_younited'));

        const cartId = this.route.snapshot.queryParamMap.get('cartId');
        if (cartId) {
            this._handlePaymentRedirect(Number(cartId));
        } else {
            // No cartId => redirect to /panier
            this.document.location.href = '/panier';
        }
    }

    ngOnDestroy(): void {
        this._ngUnsubscribe$.next(null);
        this._ngUnsubscribe$.complete();
        this.appService.hideSkeleton();
    }

    private _handlePaymentRedirect(cartId: number): void {
        this.fundingService
            .getCurrentFundingMethod(cartId)
            .pipe(takeUntil(this._ngUnsubscribe$))
            .subscribe({
                next: (data) => {
                    if (data.status === 'OK') {
                        this.tagService.send({
                            nomClic: 'Le_partenaire_a_valide_votre_demande',
                            categorieClic: 'Paiement_contact_partenaire_sortie',
                            actionClic: ACTION_CLICK.DISPLAYING,
                        });
                        this._handleFundingModeSuccess(cartId)
                            .pipe(catchError(() => of(null)))
                            .subscribe();
                    } else {
                        this._sendErrorTagYounited(data.reason.toLowerCase() ?? 'abandon_inconnu');
                        this.appService.hideSkeleton();
                        this.prismeLoggerService.sendLogToPrisme(
                            `KO Younited raison: ${data.reason.toLowerCase() ?? 'inconnu'}`,
                        );
                        if (data.reason === YounitedReason.CANCEL) {
                            // Add timeout to avoid miss of event before redirect
                            setTimeout(() => {
                                this.document.location.href = this.paymentStep.routerLink;
                            }, 250);
                        } else {
                            this.errorRedirect = true;
                        }
                    }
                },
                error: (e) => {
                    this.prismeLoggerService.sendLogToPrisme('Error during redirect process', e);
                    this.errorRedirect = true;
                    this.appService.hideSkeleton();
                },
            });
    }

    private _sendErrorTagYounited(typeRefus: string): void {
        try {
            this.tagService.send({
                nomClic: `Paiement_contact_partenaire_${typeRefus}`,
                categorieClic: 'Paiement_contact_partenaire_sortie',
                actionClic: ACTION_CLICK.DISPLAYING,
            });
        } catch (error) {
            console.log(error);
        }
    }

    private _handleFundingModeSuccess(cartId: number): Observable<boolean> {
        return this.paymentService.handleFundingModeSucess(false, cartId).pipe(
            takeUntil(this._ngUnsubscribe$),
            mergeMap((data) => {
                if (data) {
                    this.paymentData.url = data?.retourPSP?.formUrl || null;
                    this.paymentData.method = data?.retourPSP?.method || null;
                    this.paymentData.inputs = data?.retourPSP?.formInput || [];
                    this.ref.detectChanges();
                    this.paymentDataFormElement.nativeElement.submit();
                } else {
                    return of(false);
                }
            }),
        );
    }
}
